/**
 * Render-time reconciliation of saved Pharmacy Peek widgets against the
 * currently active devices (deleted device persists).
 *
 * A device removed from the site stays in the saved widget config until it is
 * manually removed. These helpers drop those stale entries before the config is
 * rendered (and, when the user saves, lets the cleaned config self-heal).
 *
 * The rules below apply to EVERY widget type, not just SystemStatus. They key
 * off the shape of `deviceId`, never the widget name:
 *  - All-system widgets (empty deviceId) are always kept.
 *  - Multi-device widgets (comma-separated deviceId, e.g. SystemStatus) are
 *    trimmed to active devices; the parallel deviceName list is trimmed by the
 *    same index when the two lists line up. An "All Systems" SystemStatus widget
 *    stores a comma-list deviceId but a single 'All Systems' deviceName label,
 *    so in that case only deviceId is trimmed and the label is left intact.
 *    The widget is dropped when none of its devices remain.
 *  - Single-device widgets are dropped when their device is no longer active.
 */
export function reconcileWidgetForActiveDevices(item: any, activeDeviceIds: string[]): any {
  const deviceId = String(item?.systemConfig?.deviceId ?? '');

  if (deviceId === '') {
    return item;
  }

  if (deviceId.includes(',')) {
    const idList = deviceId.split(',');
    const nameList = String(item?.systemConfig?.deviceName ?? '').split(',');

    // deviceName is only a per-device parallel list when its length matches the
    // id list (specific SystemStatus selection). For an "All Systems" SystemStatus
    // widget deviceName is the single label 'All Systems', so we trim deviceId
    // only and leave the label untouched.
    const namesAreParallel = nameList.length === idList.length;

    const keptIds: string[] = [];
    const keptNames: string[] = [];

    idList.forEach((id, index) => {
      if (activeDeviceIds.includes(id)) {
        keptIds.push(id);
        if (namesAreParallel) {
          keptNames.push(nameList[index]);
        }
      }
    });

    if (keptIds.length === 0) {
      return null;
    }

    return {
      ...item,
      systemConfig: {
        ...item.systemConfig,
        deviceId: keptIds.join(','),
        deviceName: namesAreParallel ? keptNames.join(',') : item?.systemConfig?.deviceName,
      },
    };
  }

  return activeDeviceIds.includes(deviceId) ? item : null;
}

export function reconcileWidgetsForActiveDevices(widgets: any[], activeDeviceIds: string[]): any[] {
  return widgets
    .map(widget => reconcileWidgetForActiveDevices(widget, activeDeviceIds))
    .filter(widget => widget != null);
}
